/**
 * SpinalTrendChart — Compact 7-day pain/mobility trend visualisation.
 * Reads /api/healing/spinal-ease/journal/trend and renders two soft
 * lines (pain descending = good, mobility ascending = good) so the
 * user can *see* the ease returning day by day.
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingDown, TrendingUp } from 'lucide-react';
import JourneyStatsPanel from './JourneyStatsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SpinalTrendChart = ({ onBack, days = 7 }) => {
  const [points, setPoints] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tRes, eRes] = await Promise.all([
          fetch(`${API}/api/healing/spinal-ease/journal/trend?days=${days}`, { headers: authHeader() }),
          fetch(`${API}/api/healing/spinal-ease/journal?days=${days}`, { headers: authHeader() }),
        ]);
        const t = await tRes.json();
        const e = await eRes.json();
        if (cancelled) return;
        setPoints(t.points || []);
        setEntries(e.entries || []);
      } catch { /* trend endpoints may be empty on first visit */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);

  // Chart geometry
  const W = 320, H = 160, P = 20;
  const xStep = points.length > 1 ? (W - P * 2) / (points.length - 1) : 0;
  const yFor = (val) => H - P - ((val / 10) * (H - P * 2));

  const painPath = useMemo(() => points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${P + i * xStep} ${yFor(p.pain_avg)}`).join(' '), [points, xStep]);
  const mobPath  = useMemo(() => points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${P + i * xStep} ${yFor(p.mobility_avg)}`).join(' '), [points, xStep]);

  const painFirst = points[0]?.pain_avg;
  const painLast  = points[points.length - 1]?.pain_avg;
  const painDelta = painFirst != null && painLast != null ? (painLast - painFirst) : null;
  const mobFirst  = points[0]?.mobility_avg;
  const mobLast   = points[points.length - 1]?.mobility_avg;
  const mobDelta  = mobFirst != null && mobLast != null ? (mobLast - mobFirst) : null;

  return (
    <motion.div
      className="fixed inset-0 overflow-y-auto px-6 py-8"
      style={{ background: 'linear-gradient(180deg, #0a0e0a 0%, #0d120d 100%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="spinal-trend-chart"
    >
      <div className="max-w-md mx-auto pt-6 pb-24">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/50 text-xs mb-6"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="trend-back"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <p className="text-white/25 text-[10px] tracking-[0.3em] uppercase mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Spinal Healing &amp; Recovery
        </p>
        <h2 className="text-white/85 text-2xl mb-6" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
          Your journey so far
        </h2>

        {/* Emotional dashboard: hours relaxed · sessions · family · streak · victories. */}
        <JourneyStatsPanel />

        {loading ? (
          <p className="text-white/40 text-sm" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            Loading…
          </p>
        ) : points.length === 0 ? (
          <div className="rounded-2xl p-6 text-center" style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px dashed rgba(255,255,255,0.08)',
          }}>
            <p className="text-white/40 text-sm mb-2" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              The pain &amp; mobility trend appears here after a few journal check-ins.
            </p>
            <p className="text-white/30 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
              After each session she&apos;ll be gently invited to record how she feels — and the trend will start to appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="rounded-2xl p-4" style={{
                background: 'rgba(200,120,110,0.05)',
                border: '1px solid rgba(200,120,110,0.15)',
              }}>
                <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Pain
                </p>
                <p className="text-white/85 text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                  {painLast?.toFixed(1) ?? '—'}
                </p>
                {painDelta != null && (
                  <p className="text-[11px] mt-1 flex items-center gap-1" style={{
                    color: painDelta < 0 ? 'rgba(180,220,180,0.9)' : painDelta > 0 ? 'rgba(230,170,150,0.85)' : 'rgba(255,255,255,0.4)',
                    fontFamily: "'Manrope', sans-serif",
                  }}>
                    {painDelta < 0 ? <TrendingDown size={11} /> : painDelta > 0 ? <TrendingUp size={11} /> : null}
                    {painDelta === 0 ? 'holding steady' : `${painDelta > 0 ? '+' : ''}${painDelta.toFixed(1)} since first day`}
                  </p>
                )}
              </div>
              <div className="rounded-2xl p-4" style={{
                background: 'rgba(140,180,150,0.05)',
                border: '1px solid rgba(140,180,150,0.15)',
              }}>
                <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Mobility
                </p>
                <p className="text-white/85 text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                  {mobLast?.toFixed(1) ?? '—'}
                </p>
                {mobDelta != null && (
                  <p className="text-[11px] mt-1 flex items-center gap-1" style={{
                    color: mobDelta > 0 ? 'rgba(180,220,180,0.9)' : mobDelta < 0 ? 'rgba(230,170,150,0.85)' : 'rgba(255,255,255,0.4)',
                    fontFamily: "'Manrope', sans-serif",
                  }}>
                    {mobDelta > 0 ? <TrendingUp size={11} /> : mobDelta < 0 ? <TrendingDown size={11} /> : null}
                    {mobDelta === 0 ? 'holding steady' : `${mobDelta > 0 ? '+' : ''}${mobDelta.toFixed(1)} since first day`}
                  </p>
                )}
              </div>
            </div>

            {/* Chart */}
            <div className="rounded-2xl p-4 mb-6" style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Last {points.length} days
              </p>
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 180 }}>
                <defs>
                  <linearGradient id="painGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(230,140,120,0.4)" />
                    <stop offset="100%" stopColor="rgba(230,140,120,0)" />
                  </linearGradient>
                  <linearGradient id="mobGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(140,190,160,0)" />
                    <stop offset="100%" stopColor="rgba(140,190,160,0.4)" />
                  </linearGradient>
                </defs>
                {/* Horizontal grid ticks */}
                {[0, 2.5, 5, 7.5, 10].map((v) => (
                  <line
                    key={v}
                    x1={P} x2={W - P}
                    y1={yFor(v)} y2={yFor(v)}
                    stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"
                  />
                ))}
                {/* Pain area + line */}
                <path d={painPath} stroke="rgba(230,140,120,0.85)" strokeWidth="1.6" fill="none" strokeLinecap="round" />
                {/* Mobility line */}
                <path d={mobPath} stroke="rgba(140,190,160,0.9)" strokeWidth="1.6" fill="none" strokeLinecap="round" />
                {/* Data points */}
                {points.map((p, i) => (
                  <g key={i}>
                    <circle cx={P + i * xStep} cy={yFor(p.pain_avg)} r={3} fill="rgba(230,140,120,0.9)" />
                    <circle cx={P + i * xStep} cy={yFor(p.mobility_avg)} r={3} fill="rgba(140,190,160,0.9)" />
                  </g>
                ))}
              </svg>
              <div className="flex justify-between text-[10px] text-white/30 mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {points.map((p, i) => (
                  <span key={i}>{p.date.slice(5)}</span>
                ))}
              </div>
              <div className="flex gap-4 justify-center mt-3">
                <span className="flex items-center gap-1.5 text-[11px] text-white/50" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: 'rgba(230,140,120,0.9)' }} /> Pain
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-white/50" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: 'rgba(140,190,160,0.9)' }} /> Mobility
                </span>
              </div>
            </div>

            {/* Recent entries list */}
            <p className="text-white/45 text-[11px] tracking-[0.25em] uppercase mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Recent entries
            </p>
            <div className="space-y-2">
              {entries.slice(0, 8).map((e) => (
                <div key={e.id} className="rounded-xl px-4 py-3 flex items-center gap-3" style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.05)',
                }} data-testid={`journal-entry-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/75 text-sm truncate" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                      {e.note || `Feeling ${e.mood}`}
                    </p>
                    <p className="text-white/30 text-[11px] mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {new Date(e.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="text-red-300/70">P {e.pain}</span>
                    <span className="text-emerald-300/70">M {e.mobility}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default SpinalTrendChart;
